class ProductGroup extends HTMLElement {
  constructor() {
    super();
    this.PRODUCTS_LIMIT = 250;
    this.TEXT_SELECTOR = '[data-atb-text]';
    this.SUBMIT_SELECTOR = '[type="submit"][name="add"]';

    const vs = window.theme?.variantStrings || {};
    this.DISABLE_TEXT = vs.unavailable || 'Unavailable';
    this.SELECT_OPTION_TEXT = vs.productSelectOption || 'Select option';

    this.atbSelectors = this.dataset.atbSelectors ? this.dataset.atbSelectors.split(',') : [];
    this.baseURL = this.dataset.baseUrl;
    this.product = this.parseJSON(this.dataset.product || '{}');
    this.productFilters = this.product.metafields?.filter || [];
    this.productFiltersKeys = Object.keys(this.productFilters);
    this.additionalFilters = this.product.additional_filters || {};
    this.additionalFiltersOrder = this.product.additional_filters_order || [];
    this.productSubFilters = this.product.sub_filters || [];
    this.collection = this.parseJSON(this.dataset.collection || '{}');
    this.collectionFilters = {};
    this.filterConfig = this.parseJSON(this.dataset.filterConfig || '{}');
    this.filters = {};
    this.subfilters = {};
    this.subfiltersKeys = [];
    this.collectionSubFilters = {};
    this.collectionSubFiltersKeys = [];
    this.filtersSet = false;
    this.products = {};
    this.matchedValues = {};
    this.filterElems = [];
    this.selectedFilters = new Map();

    this.setupEventListeners();
  }

  // Clarity patches customElements.define and triggers the constructor on registration,
  // which caused a duplicate fetch before initialization was moved here.
  // connectedCallback only fires on actual DOM insertion — safe from Clarity's interception.
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    // No filter metafield set — nothing to fetch; initialize immediately so hidden space collapses
    if (!this.productFiltersKeys?.length) {
      this.initializeProductGroup({});
      return;
    }
    this.getProducts(this.collection, this.product, (partialProducts, isFirst) => {
      if (isFirst) {
        this.initializeProductGroup(partialProducts);
      } else {
        this.refreshPicker(partialProducts);
      }
    });
  }

  setupEventListeners() {
    document.addEventListener('productGroup:change', this.onProductGroupChange.bind(this));
    this.addEventListener('change', this.onRadioChange.bind(this));
    this.addEventListener('click', this.onCrossoutClick.bind(this));
  }

  setSelectedFilter(filter, value) {
    if (this.selectedFilters.has(filter) && this.isValidValue(value)) {
      this.selectedFilters.set(filter, value);
    }
  }

  // Crossout labels sit on top of the radio input and block native click — simulate it
  onCrossoutClick(e) {
    const target = e ? e.target : null;
    if (target && target.classList.contains('filter-option__label--crossout')) {
      const input = target.closest('.filter-option')?.querySelector('[type="radio"]');
      if (input) input.click();
    }
  }

  isMainTriggerValue(filter, filterValue, subfilter) {
    const main = this.filterConfig[filter];
    const sub = main ? (main.subfilters || {})[subfilter] : null;
    const triggers = sub ? (sub.triggerValues || {}) : {};
    return triggers[filterValue] ? true : false;
  }

  findProductMatch() {
    let matched = { products: {}, productKeys: [] };
    let filtered = {};

    for (let i = 0; i < this.additionalFiltersOrder.length; i++) {
      let filter = this.additionalFiltersOrder[i];
      let value = this.selectedFilters.get(filter);

      filtered = (this.matchedValues[filter] || {})[value] || {};

      if (this.isValidValue(value)) {
        if (i === 0 || !matched.productsKeys?.length) {
          matched = filtered;
        }

        if (filtered.productsKeys && filtered.productsKeys.length) {
          if (matched.productsKeys && matched.productsKeys.length) {
            let tempMatched = { products: {}, productsKeys: [] };
            tempMatched.productsKeys = matched.productsKeys.filter((productId) => {
              if (filtered.products[productId]) {
                tempMatched.products[productId] = productId;
              }
              return filtered.products[productId];
            });

            matched = tempMatched;

            if (this.isMainFilter(filter)) {
              tempMatched = { products: {}, productsKeys: [] };
              const subfilterKeys = this.getMainSubfilterKeys(filter);

              for (let i = 0; i < subfilterKeys.length; i++) {
                const key = subfilterKeys[i];
                if (this.isMainTriggerValue(filter, value, key)) {
                  const subfilterValue = this.selectedFilters.get(key);
                  const subfiltered = (this.matchedValues[key] || {})[subfilterValue] || {};

                  if (subfiltered && subfiltered.products) {
                    tempMatched.productsKeys = matched.productsKeys.filter((productId) => {
                      if (subfiltered.products[productId]) {
                        tempMatched.products[productId] = productId;
                      }
                      return subfiltered.products[productId];
                    });

                    matched = tempMatched;
                  }
                }
              }
            }
          } else {
            break;
          }
        } else {
          matched = filtered;
          break;
        }
      }
    }

    return matched;
  }

  findProductMatchSingleDimension(name, value) {
    const keys = ((this.matchedValues[name] || {})[value] || {}).productsKeys || [];
    if (!keys.length) return null;
    const inStock = keys.find(id => this.isProductAvailable(this.products[id]));
    return this.products[inStock || keys[0]] || null;
  }

  onRadioChange(e) {
    this.setAttribute('processing', '');

    const target = e ? e.target : null;
    let productUrl = '';
    let matchedProduct = {};

    if (target) {
      this.setSelectedFilter(target.name, target.value);
    }

    this.updateFilters();

    let matched = this.findProductMatch();
    const selectedNull = [...this.selectedFilters?.values?.() || []].some(v => v === null);

    if (matched.productsKeys && matched.productsKeys.length) {
      if (!selectedNull || matched.productsKeys.length === 1) {
        matchedProduct = this.products[matched.productsKeys[0]] || {};
      }
      productUrl = matchedProduct.url;
    }

    if (!productUrl && target) {
      const fallback = this.findProductMatchSingleDimension(target.name, target.value);
      if (fallback?.url && fallback.url !== this.product.url) {
        productUrl = fallback.url;
      }
    }

    if (productUrl && productUrl !== this.product.url) {
      window.location.href = productUrl;
    } else {
      const productGroupChange = new CustomEvent('productGroup:change', {
        detail: { component: this, matchedProduct, matched }
      });
      document.dispatchEvent(productGroupChange);
    }
  }

  onProductGroupChange(e) {
    this.updateAtbElems(e);
    this.updateStatus(e);
    this.removeAttribute('processing');
  }

  updateStatus(e) {
    const statusEl = this.querySelector('[data-picker-status]');
    if (!statusEl) return;

    const { matchedProduct, matched } = (e?.detail || {});
    const selectedNull = [...this.selectedFilters?.values?.() || []].some(v => v === null);

    let msg = '';
    if (selectedNull && matched?.productsKeys?.length > 1) {
      msg = this.SELECT_OPTION_TEXT;
    } else if (!matched?.productsKeys?.length) {
      msg = this.DISABLE_TEXT;
    }
    statusEl.textContent = msg;
  }

  updateFilters() {
    if (!this.filterElems.length) return;

    this.filterElems.forEach((elem) => {
      if (!elem) return;

      const name = elem.dataset.filterName;
      const value = elem.dataset.filterVal;
      const input = elem.querySelector('[type="radio"]');
      const label = elem.querySelector('.filter-option__label');
      if (!input || !label) return;

      const parent = elem.closest(`[data-group="${name}"]`);
      const isMainFilter = parent ? parent.dataset.mainFilter === name : false;

      const { disable, oos, isMatch } = this.validFilterCheck(name, value);

      label.classList.toggle('filter-option__label--crossout', oos || disable);

      if (disable) {
        input.setAttribute('disabled', '');
        input.setAttribute('aria-disabled', 'true');
        label.classList.add('filter-option__label--disable');
      } else {
        input.removeAttribute('disabled');
        input.setAttribute('aria-disabled', 'false');
        label.classList.remove('filter-option__label--disable');
      }

      input.checked = isMatch;

      if (isMainFilter && this.selectedFilters.get(name) === value) {
        this.querySelectorAll(`[data-parent-filter="${name}"]`).forEach((subfilterElem) => {
          const subfilterKey = subfilterElem.dataset.group;
          const isParentTrigger = this.isMainTriggerValue(name, value, subfilterKey);
          subfilterElem.classList.toggle('filter-group--inactive', !isParentTrigger);
        });
      }
    });
  }

  updateAtbElems(e) {
    const { matchedProduct, matched } = (e?.detail || {});
    if (!this.atbSelectors.length) return;

    this.atbSelectors.forEach((selector) => {
      document.querySelectorAll(`${selector} ${this.SUBMIT_SELECTOR}`).forEach((elem) => {
        if (!elem) return;

        const atbTextElem = elem.querySelector(this.TEXT_SELECTOR);
        let disabled = true;
        let atbText = this.DISABLE_TEXT;

        if (matched?.productsKeys?.length > 1) {
          atbText = this.SELECT_OPTION_TEXT;
        } else if (matchedProduct?.id) {
          disabled = !this.isProductAvailable(matchedProduct);
          atbText = elem.title || window.theme?.variantStrings?.addToCart || '';
        }

        if (disabled) {
          elem.setAttribute('disabled', 'true');
        } else {
          elem.removeAttribute('disabled');
        }

        if (atbTextElem) atbTextElem.innerHTML = atbText;
      });
    });
  }

  validFilterCheck(name, value) {
    let selectedMatch = this.selectedFilters.get(name);
    let disable = false;
    let oos = false;
    let isMatch = value === selectedMatch;
    let product = {};
    let matched;
    let filtered = {};

    let currentMatches = (this.matchedValues[name] || {})[value] || {};
    if (!(currentMatches.productsKeys && currentMatches.productsKeys.length)) {
      disable = true;
    }

    const len = this.additionalFiltersOrder.length;

    if (!disable) {
      if (len < 2) {
        product = currentMatches?.productsKeys?.find((productId) => {
          return this.isProductAvailable(this.products?.[productId]);
        });
        if (!product) oos = true;
      } else if (this.isValidValue(selectedMatch)) {
        for (let i = 0; i < len; i++) {
          let filter = this.additionalFiltersOrder[i];
          let value = this.selectedFilters.get(filter);

          filtered = (this.matchedValues[filter] || {})[value] || {};

          if (filter !== name && this.isValidValue(value)) {
            if (!matched) {
              matched = filtered;
            }

            if (filtered.productsKeys && filtered.productsKeys.length) {
              if (matched.productsKeys && matched.productsKeys.length) {
                let tempMatched = { products: {}, productsKeys: [] };
                tempMatched.productsKeys = matched.productsKeys.filter((productId) => {
                  if (filtered.products[productId]) {
                    tempMatched.products[productId] = productId;
                  }
                  return filtered.products[productId];
                });
                matched = tempMatched;
              } else {
                break;
              }
            } else {
              matched = filtered;
              break;
            }
          } else if (value === null) {
            if (!matched) {
              matched = { ...currentMatches };
            }
          }
        }

        if (matched && matched.productsKeys && matched.productsKeys.length) {
          let valid = matched.productsKeys.find((productId) => {
            return currentMatches.products[productId] === productId;
          });
          product = this.products[valid];
        }

        if (!this.isProductAvailable(product)) {
          oos = true;
        }
      }
    }
    // Force OOS crossout on the current page product's own pill regardless of selected filter.
    const currentProductValue = this.productFilters[name];
    if (value === currentProductValue && !this.isProductAvailable(this.product)) {
      oos = true;
    }

    return { disable, oos, isMatch, product: product || {} };
  }

  parseJSON(str) {
    if (!str) return {};
    try {
      return JSON.parse(str);
    } catch (err) {
      console.warn('[product-group] JSON parse error', err);
      return {};
    }
  }

  initializeProductGroup(products) {
    this.products = products || {};
    this.processFilters();
    this.initializeSelectedFilters();
    this.initializedMatchedValues();
    this.renderFilters();
    this.setAttribute('initialized', '');
    document.dispatchEvent(new CustomEvent('productGroup:initialized'));
  }

  // Called for page 2+ when progressive fetch delivers more products.
  // Skips initializeSelectedFilters so user's in-progress selection is preserved.
  refreshPicker(products) {
    this.products = products;
    this.processFilters();
    this.initializedMatchedValues();
    const existingPicker = this.querySelector('.filter-picker');
    if (existingPicker) existingPicker.remove();
    this.renderFilters();
  }

  initializeSelectedFilters() {
    let selected = new Map();
    this.additionalFiltersOrder.forEach((filter) => {
      selected.set(filter, null);
    });

    selected = this.productFiltersKeys.reduce((selectedFilters, key) => {
      const isMainFilter = this.isMainFilter(key);
      const value = this.productFilters[key];

      if (this.isValidValue(value)) {
        selectedFilters.set(key, value);
      }

      if (isMainFilter) {
        const subfilters = this.getMainSubfilterKeys(key);
        if (subfilters.length) {
          subfilters.forEach((subfilter) => {
            const subfilterValues = this.getMainSubfilterValues(key, subfilter) || {};
            if (subfilterValues.valuesKeys?.length) {
              selected.set(subfilter, null);
            }
          });
        }
      }
      return selectedFilters;
    }, selected);

    selected = this.productSubFilters.reduce((selectedFilters, subfilter) => {
      const { filter, value } = subfilter;
      if (selectedFilters.has(filter) && this.isValidValue(value)) {
        selectedFilters.set(filter, value);
      }
      return selectedFilters;
    }, selected);

    this.selectedFilters = selected;
  }

  matchedValuesCheck({ product, matchedValues, filter, value }) {
    if (this.isValidValue(filter) && this.isValidValue(value)) {
      if (!matchedValues[filter]) matchedValues[filter] = {};
      if (!matchedValues[filter][value]) {
        matchedValues[filter][value] = { products: {}, productsKeys: [] };
      }
      if (!matchedValues[filter][value].products[product.id]) {
        matchedValues[filter][value].productsKeys.push(product.id);
      }
      matchedValues[filter][value].products[product.id] = product.id;
    }
    return matchedValues;
  }

  initializedMatchedValues() {
    let matchedValues = {};

    this.product.sub_filters?.forEach(({ filter, value }) => {
      matchedValues = this.matchedValuesCheck({ product: this.product, matchedValues, filter, value });
    });

    Object.values(this.products).forEach(product => {
      const filters = (product.metafields || {}).filter || {};
      Object.entries(filters).forEach(([filter, value]) => {
        matchedValues = this.matchedValuesCheck({ product, matchedValues, filter, value });
      });
    });

    this.matchedValues = matchedValues;
  }

  isProductAvailable(product) {
    if (!product) return false;
    const qty = product.inventory_quantity;
    if (qty !== undefined && qty !== null) return qty > 0;
    return !!product.available;
  }

  async getProducts(collection, product, onBatch) {
    let products = {};
    let filters = {};
    let subfilters = {};
    let page = 1;
    let hasMore = true;
    let firstBatch = true;

    if (product?.id) {
      products[product.id] = product;
    }

    if (collection?.handle) {
      const productGroup = product?.product_group || '';
      const allFilterKeys = [
        ...this.additionalFiltersOrder,
        ...this.productSubFilters.map(sf => `${sf.parent}.${sf.filter}`)
      ];
      const filterKeysParam = allFilterKeys.length ? `&filter_keys=${encodeURIComponent(allFilterKeys.join(','))}` : '';
      const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
      const themeParam = previewThemeId ? `&preview_theme_id=${encodeURIComponent(previewThemeId)}` : '';
      do {
        const url = `${this.baseURL}/collections/${collection.handle}/?view=product-group&limit=${this.PRODUCTS_LIMIT}${productGroup ? '&filter.p.m.filter.ProductGroup=' + encodeURIComponent(productGroup) : ''}${filterKeysParam}${themeParam}&page=${page}`;
        try {
          const response = await fetch(url);
          const data = response ? await response.json() : {};
          page++;

          // products_count is the total collection count, not the filtered count.
          // Paginate by batch size: if the page returned a full batch, there may be more.
          const pageCount = Object.keys(data.products || {}).length;
          hasMore = pageCount >= this.PRODUCTS_LIMIT;
          if (data.products) {
            products = Object.keys(data.products).reduce((allProducts, id) => {
              const p = data.products[id];
              if (p && !allProducts[id]) {
                allProducts[id] = p;

                const productFilters = p.metafields?.filter || {};
                if (Object.keys(productFilters).length) {
                  filters = this.additionalFiltersOrder.reduce((final, filter) => {
                    if (productFilters[filter] != null && productFilters[filter] !== '') {
                      if (!final[filter]) {
                        final[filter] = {
                          label: filter,
                          order: this.filterConfig[filter]?.order || [],
                          values: {}
                        };
                      }
                      final[filter].values[productFilters[filter]] = productFilters[filter];
                    }
                    return final;
                  }, filters);
                }

                if (this.productSubFilters?.length) {
                  subfilters = this.productSubFilters.reduce((final, filter) => {
                    const { filter: filterName, parent, value } = filter;
                    const subfilterConfig = this.filterConfig[parent]?.subfilters?.[filterName];

                    if (subfilterConfig) {
                      if (!final[filterName]) {
                        final[filterName] = {
                          label: filterName,
                          order: this.filterConfig[filterName]?.order || [],
                          values: {}
                        };
                      }
                      if (this.isValidValue(productFilters[filterName])) {
                        final[filterName].values[productFilters[filterName]] = productFilters[filterName];
                      }
                      if (this.isValidValue(value) && !final[filterName].values[value]) {
                        final[filterName].values[value] = value;
                      }
                    }
                    return final;
                  }, subfilters);
                }
              }
              return allProducts;
            }, products);
          }

          // Flush after each page so the first-batch callback sees current filter data
          this.collectionFilters = filters;
          this.collectionSubFilters = subfilters;
          this.filtersSet = true;
          if (onBatch) {
            onBatch({ ...products }, firstBatch);
            firstBatch = false;
          }
        } catch (error) {
          console.warn('[product-group] getProducts error', error);
          hasMore = false;
        }
      } while (hasMore);
    }

    return products;
  }

  isValidValue(value) {
    return (value || value === '0' || value === 0 || value === false);
  }

  processSubFilters() {
    const subfilters = this.productSubFilters.reduce((displayedFilters, filter) => {
      const { filter: filterName, parent } = filter;
      const subfilterConfig = this.filterConfig?.[parent]?.subfilters?.[filterName];

      if (subfilterConfig) {
        const values = (this.collectionSubFilters[filterName] || {}).values || {};
        const parentFilter = this.filters[parent];

        if (displayedFilters[filterName] === undefined) {
          displayedFilters[filterName] = { values: {}, parents: {}, valuesKeys: [] };
          this.subfiltersKeys.push(filterName);
        }
        displayedFilters[filterName].values = values;
        displayedFilters[filterName].valuesKeys = Object.keys(values);
        displayedFilters[filterName].parents[parent] = parent;

        if (parentFilter) {
          let parentSubfilters = parentFilter.subfilters;
          if (!parentSubfilters || !parentSubfilters.subfilters?.[filterName]) {
            if (!parentSubfilters) parentSubfilters = { subfilters: {}, subfilterKeys: [] };
            parentSubfilters.subfilters[filterName] = { values, valuesKeys: displayedFilters[filterName].valuesKeys };
            parentSubfilters.subfilterKeys.push(filterName);
          }
          this.filters[parent].subfilters = parentSubfilters;
        }
      }
      return displayedFilters;
    }, {});

    this.subfilters = subfilters;
  }

  processFilters() {
    const filters = this.additionalFiltersOrder.reduce((displayedFilters, filter) => {
      if (filter != null && this.additionalFilters[filter]) {
        if (displayedFilters[filter] === undefined) displayedFilters[filter] = {};
        displayedFilters[filter].values = (this.collectionFilters[filter] || {}).values || {};
      }
      return displayedFilters;
    }, {});

    this.productFiltersKeys.forEach(filter => {
      const value = this.productFilters[filter];
      if (filters[filter]?.values && filters[filter].values[value] === undefined && this.isValidValue(value)) {
        filters[filter].values[value] = value;
      }
    });

    this.filters = filters;
    this.processSubFilters();

    const finalAdditionalFilters = {};
    const finalAdditionalFiltersOrder = [];
    this.additionalFiltersOrder?.forEach((filter) => {
      const keys = Object.keys(this.filters[filter]?.values || {});
      const hasSubfilters = this.filters[filter]?.subfilters?.subfilterKeys?.find((k) => {
        return this.filters[filter]?.subfilters?.subfilters?.[k]?.valuesKeys?.length;
      });

      if ((keys.length > 1 || hasSubfilters) && !finalAdditionalFilters[filter]) {
        finalAdditionalFilters[filter] = filter;
        finalAdditionalFiltersOrder.push(filter);
      }
    });

    this.additionalFilters = { ...finalAdditionalFilters };
    this.additionalFiltersOrder = [...finalAdditionalFiltersOrder];
  }

  // Binary F/T Lights taxonomy — remove once values migrate to human-readable strings
  processLightsFilter(value) {
    if (value === 'T') return 'Yes';
    if (value === 'F') return 'No';
    return value;
  }

  getMainFilterSubfilters(filter) {
    return this.filters[filter]?.subfilters;
  }

  getMainSubfilterKeys(filter) {
    return this.getMainFilterSubfilters(filter)?.subfilterKeys || [];
  }

  getMainSubfilterValues(filter, subfilter) {
    return this.getMainFilterSubfilters(filter)?.subfilters?.[subfilter] || {};
  }

  isMainFilter(filter) {
    return this.getMainSubfilterKeys(filter).length > 0;
  }

  renderFilter(name, value, label) {
    if (name == null || name === '' || !this.isValidValue(value)) return '';

    const filterConfig = this.filterConfig[name] || {};
    const icon = (filterConfig.icons || {})[value];
    const { disable, oos, isMatch } = this.validFilterCheck(name, value);
    const isMainFilter = this.isMainFilter(name);

    // Stable id for label association — sanitize value for use in an id attr
    const safeVal = String(value).replace(/[^a-zA-Z0-9-]/g, '_');
    const inputId = `filter-${name}-${safeVal}`;

    const attrVal = String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<div
      class="filter-option${oos ? ' filter-option--oos' : ''}${isMainFilter ? ' filter-option--main' : ''}"
      data-filter-option
      data-filter-name="${name}"
      data-filter-val="${attrVal}">
      <input
        type="radio"
        class="filter-option__radio"
        name="${name}"
        id="${inputId}"
        value="${attrVal}"
        ${isMatch ? 'checked' : ''}
        ${disable ? 'disabled' : ''}
        aria-disabled="${disable ? 'true' : 'false'}"
        ${isMainFilter ? 'data-main-filter' : ''}
      >
      <label
        class="filter-option__label${oos || disable ? ' filter-option__label--crossout' : ''}${disable ? ' filter-option__label--disable' : ''}"
        for="${inputId}"
      >
        <span>${label || value}</span>
        ${icon ? `<img class="filter-option__icon" src="${icon}" role="presentation" alt="">` : ''}
      </label>
    </div>`;
  }

  transformFilterValue(filter, value) {
    const mapping = {
      'Lights': this.processLightsFilter
    };
    const mapFunc = mapping[filter];
    let label = this.filterConfig[filter]?.values?.[value]?.label;
    if (mapFunc) label = mapFunc(value);
    return this.isValidValue(label) ? label : value;
  }

  renderFilterGroup({ html, filter, value, used }) {
    if (used.has(value)) return { html, used };
    const label = this.transformFilterValue(filter, value);
    const str = this.renderFilter(filter, value, label);
    used.set(value, value);
    return { html: html + str, used };
  }

  renderFilters() {
    const filtersHtml = this.additionalFiltersOrder.reduce((finalHtml, filter) => {
      let filtersGroupHtml = '';
      const filterValuesOrder = (this.collectionFilters[filter] || {}).order || [];
      const filterValues = Object.values(this.filters[filter].values || {});
      let used = new Map();
      const filterLabel = this.filterConfig[filter]?.label || filter;
      const isMainFilter = this.isMainFilter(filter);
      let mainFilterCount = 0;
      let showSubfilters = false;

      let filterValuesHtml = filterValuesOrder.reduce((html, value) => {
        if (this.filters[filter]?.values && this.isValidValue(this.filters[filter].values[value])) {
          if (!used.has(value)) mainFilterCount++;
          let { html: h, used: u } = this.renderFilterGroup({ html, filter, value, used });
          used = u;
          return h;
        }
        used.set(value, value);
        return html;
      }, '');

      filterValuesHtml = filterValues.reduce((html, value) => {
        if (this.isValidValue(value)) {
          if (!used.has(value)) mainFilterCount++;
          let { html: h, used: u } = this.renderFilterGroup({ html, filter, value, used });
          used = u;
          return h;
        }
        used.set(value, value);
        return html;
      }, filterValuesHtml);

      if (filterValuesHtml) {
        filtersGroupHtml = `<fieldset
          class="filter-group"
          data-group="${filter}"
          ${isMainFilter ? `data-main-filter="${filter}"` : ''}>
          <legend class="filter-group__label">${filterLabel}</legend>
          <div class="filter-group__options">
            ${filterValuesHtml}
          </div>
        </fieldset>`;

        if (isMainFilter) {
          let subUsed = new Map();
          const mainSubfilters = this.filters[filter]?.subfilters;
          let subfiltersGroupHtml = '';
          let subfilterValuesHtml = '';

          mainSubfilters?.subfilterKeys?.forEach((subfilter) => {
            let subFilterCount = 0;
            const values = mainSubfilters.subfilters[subfilter]?.valuesKeys || [];
            const selectedParentValue = this.selectedFilters.get(filter);
            let showFilter = false;

            subfilterValuesHtml = values.reduce((html, value) => {
              if (!showFilter && this.filterConfig[filter]?.subfilters?.[subfilter]?.triggerValues?.[selectedParentValue]) {
                showFilter = true;
              }
              if (!subUsed.has(value)) subFilterCount++;
              let { html: h, used: u } = this.renderFilterGroup({ html, filter: subfilter, value, used: subUsed });
              subUsed = u;
              return h;
            }, subfilterValuesHtml);

            if (subfilterValuesHtml && subFilterCount > 1) {
              subfiltersGroupHtml += `<fieldset
                class="filter-group${showFilter ? '' : ' filter-group--inactive'}"
                data-group="${subfilter}"
                data-sub-filter="${subfilter}"
                data-parent-filter="${filter}">
                <legend class="filter-group__label">${subfilter}</legend>
                <div class="filter-group__options">
                  ${subfilterValuesHtml}
                </div>
              </fieldset>`;
            }
          });

          if (subfiltersGroupHtml) {
            filtersGroupHtml += subfiltersGroupHtml;
            showSubfilters = true;
          }
        }
      }

      if (!showSubfilters && mainFilterCount < 2) filtersGroupHtml = '';

      return finalHtml + filtersGroupHtml;
    }, '');

    if (filtersHtml) {
      const wrapper = document.createElement('div');
      wrapper.classList.add('filter-picker');
      wrapper.innerHTML = filtersHtml;
      this.prepend(wrapper);
      this.filterElems = [...this.querySelectorAll('[data-filter-option]')];
    } else {
      this.style.display = 'none';
    }
  }
}

customElements.define('product-group', ProductGroup);

// HeightSwitcher — Size-only picker for olive tree families.
// Inherits all ProductGroup logic. Override renderFilters() here when
// a distinct visual treatment is confirmed by design.
class HeightSwitcher extends ProductGroup {}

customElements.define('height-switcher', HeightSwitcher);
